import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { ChevronRight } from "lucide-react";
import { PageHeader, StatCard, inr } from "@/components/platform/platformUi";

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    } catch {
        return "—";
    }
}

const SUB_STYLE = {
    trial: "bg-[#FDF3DC] text-[#9A7B10]",
    active: "bg-[#DCEEDC] text-[#1F7A55]",
    past_due: "bg-[#FEF5F3] text-[#C84B31]",
    cancelled: "bg-[#E0EDFA] text-[#2E6BA8]",
    expired: "bg-[#F3F0EB] text-[#8A8178]",
};

export default function PlatformSubscriptions() {
    const [rows, setRows] = useState([]);
    const [status, setStatus] = useState("");
    const [trial, setTrial] = useState("");

    const load = () => {
        api.get("/platform/subscriptions", { params: { status, trial } })
            .then((r) => setRows(r.data || []))
            .catch(() => toast.error("Could not load subscriptions"));
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, trial]);

    const totalMrr = rows.reduce((s, r) => s + (r.mrr || 0), 0);
    const trialCount = rows.filter((r) => (r.subscription_status || "trial") === "trial").length;
    const activeCount = rows.filter((r) => r.subscription_status === "active").length;
    const pastDueCount = rows.filter((r) => r.subscription_status === "past_due").length;

    return (
        <div className="space-y-6 p-6 lg:p-8" data-testid="platform-subscriptions-page">
            <PageHeader
                eyebrow="Tenants"
                title="Subscriptions"
                subtitle="Trial status, active plans, and recurring revenue across all salons"
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard label="Listed" value={rows.length} sub="Matching filters" />
                <StatCard label="On trial" value={trialCount} accent="#FDF3DC" iconColor="#9A7B10" />
                <StatCard label="Active paid" value={activeCount} accent="#DCEEDC" iconColor="#2D9B6E" />
                <StatCard label="MRR (filtered)" value={inr(totalMrr)} sub={`${pastDueCount} past due`} accent="#FBE4EF" iconColor="#D9468F" />
            </div>

            <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                    <Label className="text-[11px] font-bold uppercase text-[#3D2817]">Subscription status</Label>
                    <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        className="h-9 rounded-md border border-[#E8E4DE] bg-white px-3 text-sm font-medium text-[#3D2817]"
                    >
                        <option value="">All</option>
                        <option value="trial">Trial</option>
                        <option value="active">Active</option>
                        <option value="past_due">Past due</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="expired">Expired</option>
                    </select>
                </div>
                <div className="space-y-1">
                    <Label className="text-[11px] font-bold uppercase text-[#3D2817]">Trial window</Label>
                    <select
                        value={trial}
                        onChange={(e) => setTrial(e.target.value)}
                        className="h-9 rounded-md border border-[#E8E4DE] bg-white px-3 text-sm font-medium text-[#3D2817]"
                    >
                        <option value="">All</option>
                        <option value="active">Trial active</option>
                        <option value="expired">Trial expired</option>
                    </select>
                </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-[#E8E4DE] bg-white">
                <table className="min-w-max w-full border-collapse">
                    <thead>
                        <tr className="border-b border-[#E8E4DE] text-left text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#8A8178]">
                            <th className="px-4 py-3">Salon</th>
                            <th className="px-4 py-3">Owner</th>
                            <th className="px-4 py-3">Plan</th>
                            <th className="px-4 py-3">Subscription</th>
                            <th className="px-4 py-3">Trial</th>
                            <th className="px-4 py-3">Trial ends</th>
                            <th className="px-4 py-3">Period ends</th>
                            <th className="px-4 py-3">MRR</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} className="border-b border-[#E8E4DE]/50 last:border-b-0">
                                <td className="px-4 py-3">
                                    <Link
                                        to={`/platform/salons/${r.id}`}
                                        className="group flex items-center gap-1 font-semibold text-[#4A0E17] hover:text-[#C9A227]"
                                    >
                                        {r.name}
                                        <ChevronRight className="size-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
                                    </Link>
                                    <div className="text-xs text-[#8A8178]">{r.city || r.slug}</div>
                                </td>
                                <td className="px-4 py-3 text-sm text-[#3D2817]">{r.owner?.email || "—"}</td>
                                <td className="px-4 py-3 text-sm font-semibold text-[#3D2817]">{r.plan?.name || "—"}</td>
                                <td className="px-4 py-3">
                                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${SUB_STYLE[r.subscription_status] || SUB_STYLE.trial}`}>
                                        {r.subscription_status || "trial"}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-sm capitalize text-[#3D2817]">{r.trial_status || "—"}</td>
                                <td className="px-4 py-3 text-sm text-[#8A8178]">{formatDate(r.trial_ends_at)}</td>
                                <td className="px-4 py-3 text-sm text-[#8A8178]">{formatDate(r.current_period_end)}</td>
                                <td className="px-4 py-3 text-sm font-bold text-[#4A0E17]">{inr(r.mrr)}</td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={8} className="px-4 py-10 text-center text-sm text-[#8A8178]">
                                    No subscriptions match these filters
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
